import { z } from 'zod';
import { LineItemTypeOptions } from '../types/OcrTypes';
import { valueToFixedNumber } from './OcrInvoiceHelpers';
function addLineItemUnitPriceValidation(schema) {
    return schema.superRefine((values, ctx) => {
        if (values.type !== LineItemTypeOptions.FEE && valueToFixedNumber(values.unitPrice) <= 0) {
            ctx.addIssue({
                message: 'Unit price must be greater than 0',
                code: z.ZodIssueCode.custom,
                path: ['unitPrice'],
            });
            return;
        }
        if (values.type === LineItemTypeOptions.FEE && valueToFixedNumber(values.unitPrice) === 0) {
            ctx.addIssue({
                message: 'Unit price can not be 0',
                code: z.ZodIssueCode.custom,
                path: ['unitPrice'],
            });
            return;
        }
    });
}
const LineItemTaxSchema = z.object({
    percent: z.number().optional(),
    dollarAmount: z.number().optional()
});
const DEFAULT_UNITS_VALUE = ["each", "pack", "bunch", "case", "kg", "g", "lb", "litre", "gallon", "oz", "dozen", "keg"];
const stringOrNumberMin1 = z.union([z.number().min(1), z.string().min(1)]);
const stringOrNumberOptional = z.union([z.number().optional(), z.string().optional()]);
export const LineItemSchema = z.object({
    name: z.string().min(1, { message: 'Name is required' }),
    sourceUUID: z.string().optional(),
    categoryID: z.string().optional(),
    sku: z.string().min(1),
    unit: z.string().min(1).refine((value) => DEFAULT_UNITS_VALUE.includes(value)),
    quantity: stringOrNumberMin1,
    unitPrice: z.union([z.number(), z.string().min(1)]),
    weight: stringOrNumberOptional,
    taxCode: z.string().optional(),
    tax: LineItemTaxSchema.optional(),
    taxable: z.boolean().optional(),
    total: z.number().optional(),
    key: z.string().optional(),
    inventoryItemKey: z.string().optional(),
    packageID: z.string().optional().nullable(),
    updateCatalog: z.boolean().optional(),
    productKey: z.string().optional(),
    originalWeight: stringOrNumberOptional,
    hasFinalSalesQuantitySet: z.boolean().optional(),
    removedFromOcrInvoice: z.boolean().optional(),
    taxIsSet: z.boolean().optional(),
    glCodeID: z.string().optional(),
    taxCodeID: z.string().optional(),
    unitDeposit: z.number().optional(),
    discount: stringOrNumberOptional,
    reactKey: z.string().optional(),
    type: z.enum([LineItemTypeOptions.PRODUCT, LineItemTypeOptions.TAX, LineItemTypeOptions.FEE]),
});
export const LineItemSchemaWithGlcodeIDRequired = LineItemSchema.extend({
    glCodeID: z.string().min(1, { message: 'GL Code is required' }),
});
export const LineItemSchemaWithGlcodeIDRequiredExceptTaxItem = LineItemSchema.superRefine((values, ctx) => {
    if (values.type !== LineItemTypeOptions.TAX && !values.glCodeID) {
        ctx.addIssue({
            message: 'GL Code is required',
            code: z.ZodIssueCode.custom,
            path: ['glCodeID'],
        });
    }
});
const getInvoiceOcrLineItemsSchema = (glCodeIDRequired = false, isCustomTax = false) => {
    const schemaBase = glCodeIDRequired ?
        isCustomTax ? LineItemSchemaWithGlcodeIDRequired : LineItemSchemaWithGlcodeIDRequiredExceptTaxItem
        : LineItemSchema;
    const schema = addLineItemUnitPriceValidation(schemaBase);
    return z.array(schema).min(1).refine((items) => {
        const skus = items.map((item) => item.sku);
        return new Set(skus).size === skus.length;
    }, { message: 'SKU must be unique' });
};
/*
 * These schemas are for the line items that are returned from the OCR service and are used for the form validation
 */
export const InvoiceOcrLineItemsSchema = getInvoiceOcrLineItemsSchema();
export const InvoiceOcrLineItemsSchemaWithGlcodeIDRequired = getInvoiceOcrLineItemsSchema(true, true);
export const InvoiceOcrLineItemsSchemaWithGlcodeIDRequiredExceptTaxItem = getInvoiceOcrLineItemsSchema(true, false);
export const sanitizeExternalReferenceID = (externalReferenceID) => externalReferenceID === null || externalReferenceID === void 0 ? void 0 : externalReferenceID.replace('/', '');
//# sourceMappingURL=OcrInvoiceValidator.js.map